import { observer } from 'mobx-react'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import Axios from 'axios'
import YAML from 'js-yaml'
import CheckboxField from '~components/model-form/CheckboxField'
import Fieldset from '~components/model-form/Fieldset'
import ModelForm, { IModelFormRenderProps } from '~components/model-form/ModelForm'
import SelectField from '~components/model-form/SelectField'
import SimpleField from '~components/model-form/SimpleField'
import { IResourceRoutesFormProps } from '~components/routing/ResourceRoutes'
import Config from '~core/config/Config'
import PageUri from '~core/enums/pages'
import useStore from '~store/hooks/useStore'
import { Concession } from '~store/models/Concession'
import { LineConfiguration } from '~store/models/LineConfiguration'
import { sortBy } from 'lodash'

// ███████╗ ██████╗ ██████╗ ███╗   ███╗
// ██╔════╝██╔═══██╗██╔══██╗████╗ ████║
// █████╗  ██║   ██║██████╔╝██╔████╔██║
// ██╔══╝  ██║   ██║██╔══██╗██║╚██╔╝██║
// ██║     ╚██████╔╝██║  ██║██║ ╚═╝ ██║
// ╚═╝      ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝
//

const PrimaryJourneyAlgorithmOptions = {
	LongestJourney: 'Langste route',
	MostTrips: 'Meeste ritten',
	P25: 'Alle routes met 25% of meer van de ritten',
}

const LineFormMain = observer(({ draft, resources, actionButtons }: IModelFormRenderProps<LineConfiguration>) => {
	const config = draft.data

	const concessions = resources.concessions as Concession[]
	const concessionOptions = React.useMemo(() => {
		const options: { [key: string]: string } = {}
		concessions.forEach(c => (options[c.slug] = c.slug))
		return options
	}, [concessions])

	const categoryKeys = resources.categoryKeys as { key: string; label: string }[]
	const categoryOptions = React.useMemo(() => {
		const options: { [key: string]: string } = {}
		categoryKeys.forEach(c => (options[c.key] = c.label))
		return options
	}, [categoryKeys])

	return (
		<React.Fragment>
			{/* Algemeen */}
			<Fieldset title='Configuratie'>
				<SimpleField attribute={'name'} label={'Naam'} required />
				<SelectField attribute={'category_key'} label={'Categorie'} options={categoryOptions} />
				<SelectField attribute={'concession_slug'} label={'Concessie'} required options={concessionOptions} />
				<SelectField
					attribute={'primary_journey_algorithm'}
					label={'Algoritme voor het bepalen van de primaire routes'}
					required
					options={PrimaryJourneyAlgorithmOptions}
				/>
				<SimpleField
					attribute={'away_destination_name'}
					grid={{ xs: 6 }}
					label={'Bestemming heen-richting'}
					field={{ help: 'Als je dit wilt overschrijven in de dienstregeling' }}
				/>
				<SimpleField attribute={'return_destination_name'} grid={{ xs: 6 }} label={'Bestemming terug-richting'} />

				<SimpleField
					attribute={'color'}
					label={'Kleur'}
					input={{ type: 'color' }}
					field={{ help: 'Vul optioneel een HEX-code in voor de kleur van de lijn in het lijnen-overzicht' }}
					nullWhenEmpty
				/>

				<CheckboxField attribute={'is_hidden'} label={'Lijn niet tonen op website'} />
			</Fieldset>

			{/* Actions */}
			<Fieldset>{actionButtons}</Fieldset>
		</React.Fragment>
	)
})

// ██╗    ██╗██████╗  █████╗ ██████╗ ██████╗ ███████╗██████╗
// ██║    ██║██╔══██╗██╔══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
// ██║ █╗ ██║██████╔╝███████║██████╔╝██████╔╝█████╗  ██████╔╝
// ██║███╗██║██╔══██╗██╔══██║██╔═══╝ ██╔═══╝ ██╔══╝  ██╔══██╗
// ╚███╔███╔╝██║  ██║██║  ██║██║     ██║     ███████╗██║  ██║
//  ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝     ╚═╝     ╚══════╝╚═╝  ╚═╝
//

const LineForm = ({ id, newModel }: IResourceRoutesFormProps) => {
	const history = useHistory()
	const { api } = useStore()

	return (
		<ModelForm<LineConfiguration>
			model={LineConfiguration}
			modelId={id}
			newModel={newModel}
			allowDelete={false}
			paper
			header={{
				backUri: PageUri.Lines,
			}}
			container
			loadResources={async record => {
				// Load concessions
				const { records: concessions } = await api.all(Concession)

				// Load category keys from yaml
				let categoryKeys: { key: string; label: string }[] = []
				try {
					// Load the yaml file (using Dutch as default)
					const response = await Axios.get(`${Config.langUrl}/nl/website-${Config.operator}.yaml`, {
						params: {
							cacheBust: new Date().getTime(),
						},
					})
					const yamlData = YAML.safeLoad(response.data) as any

					// Extract category keys from the yaml
					// The structure is typically: categories: { key: "label", ... }
					if (yamlData.lines.categories) {
						categoryKeys = sortBy(
							Object.keys(yamlData.lines.categories).map(key => ({
								key,
								label: yamlData.lines.categories[key] || key,
							})),
							'label'
						)
					}
				} catch (error) {
					console.error('Failed to load category keys from yaml:', error)
				}

				return { concessions, categoryKeys }
			}}
			render={LineFormMain}
			renderTitle={({ record }) => (newModel ? 'Nieuw config' : `Lijn ${record.line_number}: ${record.name}`)}
		/>
	)
}
export default observer(LineForm)
